import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Checkbox,
  DatePicker,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
} from "../../../../components";
import { value } from "../../../../helpers";
import {
  ColecoesService,
  MarcasService,
  ModeloGradeService,
  PessoaService,
  SessionService,
  TagsService,
} from "../../../../services";
import {
  formatDate,
  responseErros,
  timeoutBuscaAutocomplete,
} from "../../../../utils";
import { useFiltrosRelatoriosHandlers } from "../../components/use-filtros-relatorios-handlers";

const colecoesService = new ColecoesService();
const marcasService = new MarcasService();
const modeloGradeService = new ModeloGradeService();
const pessoaService = new PessoaService();
const sessionService = new SessionService();
const tagsService = new TagsService();
const CAMPO_MODELO_GRADE = "Modelo de Grade";
const CAMPO_VARIACAO = { x: "valorEixoX", y: "valorEixoY" };
const FILTROS_PRODUTO = {
  tagsId: { rotulo: "Tags", campo: "descricao" },
  marcasId: { rotulo: "Marcas", campo: "nome" },
  colecoesId: { rotulo: "Coleções", campo: "descricao" },
};

const formatarRotuloVariacao = (descricao) =>
  `${descricao.charAt(0)}${descricao.slice(1).toLowerCase()}`;

const FormFiltroAvancadoGradeClientes = ({
  filtrosAvancados,
  setFiltrosAvancados,
  filtrosAplicadosImpressao,
  setFiltrosAplicadosImpressao,
  setValidationError,
}) => {
  const inputRefCliente = useRef();
  const variacoesAnteriores = useRef({ modeloGradeId: null, rotulos: [] });
  const [modeloGradeList, setModeloGradeList] = useState([]);
  const [clienteList, setClienteList] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [marcaList, setMarcaList] = useState([]);
  const [colecaoList, setColecaoList] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);
  const usuario = sessionService.getUsuario();
  const empresa = sessionService.getEmpresa();
  const {
    onChangeAutocomplete,
    onChangeDate,
    onChangeCheckbox,
    onChangeInput,
  } = useFiltrosRelatoriosHandlers(
    setFiltrosAvancados,
    setFiltrosAplicadosImpressao
  );

  useEffect(() => {
    setEstadoInicial();
    buscarModelosGrade();
    buscarListaFiltro(
      tagsService,
      { nonPaginated: true, ativado: true, tipo: "PRODUTO" },
      setTagList
    );
    buscarListaFiltro(
      marcasService,
      { nonPaginated: true, ativado: true },
      setMarcaList
    );
    buscarListaFiltro(
      colecoesService,
      { nonPaginated: true, ativado: true },
      setColecaoList
    );
    return () => removerErroModeloGrade();
  }, []);

  const modeloSelecionado = modeloGradeList.find(
    ({ id }) => id === filtrosAvancados?.modeloGradeId
  );
  const variacoesModelo = (modeloSelecionado?.variacaoModeloGrade ?? [])
    .filter(({ tipoEixo }) => CAMPO_VARIACAO[tipoEixo])
    .sort((variacao, outraVariacao) =>
      variacao.tipoEixo.localeCompare(outraVariacao.tipoEixo)
    )
    .map(({ id, descricao, tipoEixo, valorVariacaoModeloGrade }) => ({
      id,
      campo: CAMPO_VARIACAO[tipoEixo],
      rotulo: formatarRotuloVariacao(descricao),
      valores: (valorVariacaoModeloGrade ?? []).map(({ valor }) => valor),
    }));

  useEffect(() => {
    const anterior = variacoesAnteriores.current;
    const mudouModelo =
      anterior.modeloGradeId !== filtrosAvancados?.modeloGradeId;
    if (mudouModelo && anterior.rotulos.length) {
      limparFiltroVariacoes(anterior.rotulos);
    }
    variacoesAnteriores.current = {
      modeloGradeId: filtrosAvancados?.modeloGradeId,
      rotulos: variacoesModelo.map(({ rotulo }) => rotulo),
    };
  }, [filtrosAvancados?.modeloGradeId, variacoesModelo.length]);

  useEffect(() => {
    if (filtrosAvancados?.modeloGradeId) {
      removerErroModeloGrade();
      return;
    }
    setValidationError((prevState) =>
      prevState.includes(CAMPO_MODELO_GRADE)
        ? prevState
        : [...prevState, CAMPO_MODELO_GRADE]
    );
  }, [filtrosAvancados?.modeloGradeId]);

  function limparFiltroVariacoes(rotulos) {
    setFiltrosAvancados((prevState) => {
      const filtros = { ...prevState };
      Object.values(CAMPO_VARIACAO).forEach((campo) => delete filtros[campo]);
      return filtros;
    });
    setFiltrosAplicadosImpressao((prevState) => {
      const filtros = { ...prevState };
      rotulos.forEach((rotulo) => delete filtros[rotulo]);
      return filtros;
    });
  }

  const onChangeVariacao = ({ campo, rotulo }, valor) => {
    setFiltrosAvancados((prevState) => {
      const filtros = { ...prevState, [campo]: valor };
      if (!valor) delete filtros[campo];
      return filtros;
    });
    setFiltrosAplicadosImpressao((prevState) => {
      const filtros = { ...prevState, [rotulo]: valor };
      if (!valor) delete filtros[rotulo];
      return filtros;
    });
  };

  function removerErroModeloGrade() {
    setValidationError((prevState) =>
      prevState.filter((campo) => campo !== CAMPO_MODELO_GRADE)
    );
  }

  function setEstadoInicial() {
    const hoje = new Date();
    const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const dataEmissaoStart = formatDate.toSend(inicioMes);
    const dataEmissaoEnd = formatDate.toSend(hoje);

    setFiltrosAvancados({
      ...filtrosAvancados,
      empresasIds: [empresa.id],
      ordenarPor: "nomeCliente",
      dataEmissaoStart,
      dataEmissaoEnd,
      porIntervalo: [
        {
          coluna: "dataEmissao",
          de: dataEmissaoStart,
          ate: dataEmissaoEnd,
        },
      ],
    });

    const filtrosImpressao = {
      ...filtrosAplicadosImpressao,
      ["Data-Emissão-De"]: format(inicioMes, "dd/MM/yyyy"),
      ["Data-Emissão-Até"]: format(hoje, "dd/MM/yyyy"),
    };
    if (usuario.acessosEmpresas?.length) {
      filtrosImpressao["Empresas-Associadas"] = empresa.razaoSocial;
    }
    setFiltrosAplicadosImpressao(filtrosImpressao);
  }

  const buscarModelosGrade = () => {
    modeloGradeService.getAll({ nonPaginated: true }).then((result) => {
      if (!result.isAxiosError) {
        setModeloGradeList(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const buscarListaFiltro = (service, filtros, setLista) => {
    service.getAll(filtros).then((result) => {
      if (!result.isAxiosError) {
        setLista(result.data);
      } else {
        responseErros(result);
      }
    });
  };

  const onChangeMultipleProduto = (valores, name) => {
    const { rotulo, campo } = FILTROS_PRODUTO[name];
    setFiltrosAvancados((prevState) => {
      const filtros = { ...prevState, [name]: valores.map(({ id }) => id) };
      if (!valores.length) delete filtros[name];
      return filtros;
    });
    setFiltrosAplicadosImpressao((prevState) => {
      const filtros = {
        ...prevState,
        [rotulo]: valores.map((valor) => valor[campo]).join(" | "),
      };
      if (!valores.length) delete filtros[rotulo];
      return filtros;
    });
  };

  const buscarClientes = (filtros) => {
    pessoaService
      .getAll({ nonPaginated: true, tiposPessoaId: 1, ...filtros })
      .then((result) => {
        if (!result.isAxiosError) {
          setClienteList(result.data);
        } else {
          responseErros(result);
        }
      });
    setLoadingAutoComplete(false);
  };

  const onInputChangeCliente = (event) => {
    if (
      event.key === "Enter" &&
      inputRefCliente.current === document.activeElement &&
      event.target?.value?.length > 2
    ) {
      setLoadingAutoComplete(true);
      const filtros = {
        nomeRazaoSocial: event.target.value,
        apelidoFantasia: event.target.value,
      };
      timeoutBuscaAutocomplete(buscarClientes(filtros));
    }
  };

  function onChangeMultipleEmpresas(empresas) {
    setFiltrosAvancados((prevState) => ({
      ...prevState,
      empresasIds: empresas.map(({ id }) => id),
    }));
    setFiltrosAplicadosImpressao((prevState) => ({
      ...prevState,
      "Empresas-Associadas": empresas
        .map(({ razaoSocial }) => razaoSocial)
        .join(" | "),
    }));
  }

  return (
    <>
      {usuario.acessosEmpresas?.length > 0 && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                multiple
                id="empresasIds"
                name="empresasIds"
                options={usuario.acessosEmpresas}
                noOptionsText="Sem opções"
                autoHighlight
                disablePortal
                filterSelectedOptions
                getOptionLabel={(option) => option?.razaoSocial || ""}
                onChange={(_, newValue) => onChangeMultipleEmpresas(newValue)}
                value={value.multiple(
                  usuario.acessosEmpresas || [],
                  filtrosAvancados["empresasIds"]
                    ? filtrosAvancados["empresasIds"]
                    : ""
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    required
                    label="Empresas"
                    margin="normal"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
        </>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="modeloGradeId"
              name="modeloGradeId"
              options={modeloGradeList}
              noOptionsText="Sem opções"
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.nome ?? ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Modelo de Grade"
                  variant="outlined"
                  required
                />
              )}
              onChange={(_, newValue) =>
                onChangeAutocomplete(
                  "modeloGradeId",
                  newValue,
                  "Modelo-de-Grade"
                )
              }
              value={value.autoComplete(
                modeloGradeList,
                filtrosAvancados?.modeloGradeId
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      {variacoesModelo.length > 0 && (
        <Grid container spacing={2}>
          {variacoesModelo.map((variacao) => (
            <Grid item xs={6} key={variacao.id}>
              <FormControl variant="outlined" margin="normal" fullWidth>
                <Autocomplete
                  id={variacao.campo}
                  name={variacao.campo}
                  options={variacao.valores}
                  noOptionsText="Sem opções"
                  autoHighlight
                  disablePortal
                  getOptionLabel={(option) => option ?? ""}
                  onChange={(_, newValue) =>
                    onChangeVariacao(variacao, newValue)
                  }
                  value={filtrosAvancados?.[variacao.campo] ?? null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={variacao.rotulo}
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            </Grid>
          ))}
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl variant="outlined" margin="normal" fullWidth>
            <Autocomplete
              id="clienteId"
              name="Cliente"
              options={clienteList}
              autoHighlight
              disablePortal
              getOptionLabel={(option) => option?.nomeRazaoSocial ?? ""}
              getOptionSelected={(option, value) =>
                option?.nomeRazaoSocial === value?.nomeRazaoSocial
              }
              loading={loadingAutoComplete}
              loadingText="Carregando"
              noOptionsText="Digite e pressione Enter"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente"
                  variant="outlined"
                  onKeyDown={onInputChangeCliente}
                  inputRef={inputRefCliente}
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
              onChange={(_, newValue) =>
                onChangeAutocomplete("clienteId", newValue, "Cliente")
              }
              value={value.autoComplete(
                clienteList,
                filtrosAvancados?.clienteId
              )}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            id="tagsId"
            name="tagsId"
            options={tagList}
            noOptionsText="Sem opções"
            autoHighlight
            disablePortal
            filterSelectedOptions
            getOptionLabel={(option) => option?.descricao || ""}
            renderOption={(props, option) => (
              <li {...props}>
                <div className="d-flex align-items-center gap-2 w-100">
                  <i
                    className="ph-fill ph-bookmark-simple"
                    style={{ color: option.cor, fontSize: 18 }}
                  />
                  <div>{option.descricao}</div>
                </div>
              </li>
            )}
            onChange={(_, newValue) =>
              onChangeMultipleProduto(newValue, "tagsId")
            }
            value={value.multiple(tagList, filtrosAvancados?.tagsId)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                margin="normal"
                label="Tags"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            multiple
            id="marcasId"
            name="marcasId"
            options={marcaList}
            noOptionsText="Sem opções"
            autoHighlight
            disablePortal
            filterSelectedOptions
            getOptionLabel={(option) => option?.nome || ""}
            onChange={(_, newValue) =>
              onChangeMultipleProduto(newValue, "marcasId")
            }
            value={value.multiple(marcaList, filtrosAvancados?.marcasId)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                margin="normal"
                label="Marcas"
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            multiple
            id="colecoesId"
            name="colecoesId"
            options={colecaoList}
            noOptionsText="Sem opções"
            autoHighlight
            disablePortal
            filterSelectedOptions
            getOptionLabel={(option) => option?.descricao || ""}
            onChange={(_, newValue) =>
              onChangeMultipleProduto(newValue, "colecoesId")
            }
            value={value.multiple(colecaoList, filtrosAvancados?.colecoesId)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                margin="normal"
                label="Coleções"
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <DatePicker
            id="dataEmissaoStart"
            name="Data-Emissão-De"
            label="Data de Emissão De"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataEmissaoStart)}
            onChange={(date) =>
              onChangeDate("dataEmissaoStart", date, "Data-Emissão-De")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
        <Grid item xs={6}>
          <DatePicker
            id="dataEmissaoEnd"
            name="Data-Emissão-Até"
            label="Data de Emissão Até"
            margin
            format="dd/MM/yyyy"
            value={value.date(filtrosAvancados?.dataEmissaoEnd)}
            onChange={(date) =>
              onChangeDate("dataEmissaoEnd", date, "Data-Emissão-Até")
            }
            slotProps={{
              popper: { sx: { zIndex: 2000 } },
            }}
            setError={setValidationError}
          />
        </Grid>
      </Grid>
      <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Select
            id="ordenarPor"
            name="ordenarPor"
            margin="normal"
            value={value.text(filtrosAvancados?.ordenarPor)}
            label="Ordenar Clientes Por"
            onChange={(event) => onChangeInput(event, "ordenarPor")}
            fullWidth
            SelectProps={{
              MenuProps: {
                disablePortal: true,
                PaperProps: {
                  sx: {
                    zIndex: 1600,
                  },
                },
              },
            }}
          >
            <MenuItem value={"nomeCliente"}>Alfabética</MenuItem>
            <MenuItem value={"codigoCliente"}>Numérica</MenuItem>
          </Select>
        </Grid>
      </Grid>
      {empresa.moduloServico && (
        <>
          <Divider sx={{ mt: 2, mb: 1, border: "1px solid grey" }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                label="Considerar Ordem de Serviço"
                labelPlacement="end"
                control={
                  <Checkbox
                    name="ordemServico"
                    checked={filtrosAvancados?.ordemServico || false}
                    onChange={(event) =>
                      onChangeCheckbox(event, "Ordem-Servico")
                    }
                    color="primary"
                  />
                }
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default FormFiltroAvancadoGradeClientes;
